import type { PhrasingContent } from 'mdast';

import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import type { FromPMHandler, FromPMHandlers } from '../types';
import { convertInlineChildren } from '../utils';

export function tableCell(
	node: PMNode,
	handlers: FromPMHandlers,
): { children: PhrasingContent[]; type: 'tableCell' } {
	const children: PhrasingContent[] = [];
	// Table cells contain paragraphs wrapping inline content; flatten them.
	// Use convertInlineChildren so non-text inline nodes (hard_break, image,
	// inlineCode, etc.) are handled via the handlers map rather than dropped.
	node.forEach((child) => {
		if (child.type.name === 'paragraph') {
			children.push(...convertInlineChildren(child, handlers));
		}
	});
	return { type: 'tableCell' as const, children };
}

/**
 * Flatten the blocks in a GFM table cell, which only accepts phrasing content.
 * Separate blocks with hard breaks (serialized as spaces inside a cell) so words don't merge.
 */
export const tableCellContent = (node: PMNode, handlers: FromPMHandlers): PhrasingContent[] => {
	const blocks: PhrasingContent[][] = [];
	node.forEach((child) => {
		const content = cellBlockContent(child, handlers);
		if (content.length > 0) {
			blocks.push(content);
		}
	});
	return blocks.flatMap((content, index) =>
		index === 0 ? content : [{ type: 'break' as const }, ...content],
	);
};

const paragraphChildren = (result: ReturnType<FromPMHandler>): PhrasingContent[] =>
	result && !Array.isArray(result) && result.type === 'paragraph' ? result.children : [];

const cellBlockContent = (node: PMNode, handlers: FromPMHandlers): PhrasingContent[] => {
	const name = node.type.name;
	if (name === 'codeBlock' || name === 'code_block') {
		return node.textContent ? [{ type: 'inlineCode', value: node.textContent }] : [];
	}
	const handler = handlers[name];
	if (!handler) {
		// No handler: use the unsupported-node fallback, matching unknown nodes outside tables.
		return paragraphChildren(handlers.onUnsupportedNode?.(node));
	}
	if (name === 'mediaSingle' || name === 'media_single') {
		// Let the media handler select its media child; it intentionally omits captions.
		return paragraphChildren(handler(node));
	}
	if (node.isTextblock) {
		// Paragraphs and headings: keep inline nodes and marks.
		return convertInlineChildren(node, handlers);
	}
	if (node.childCount === 0) {
		// Leaf blocks such as media: keep whatever inline content their handler produces.
		return paragraphChildren(handler(node));
	}
	// Containers (lists, panels, blockquotes): flatten their blocks.
	return tableCellContent(node, handlers);
};
